import axios from "axios";

const BASE_URL =
  "https://backdanlu.eu-4.evennode.com";

const getToken = () =>
  localStorage.getItem("token") || "";

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
});

/* =========================================
   TOKEN
========================================= */

api.interceptors.request.use(
  (config) => {
    config.headers =
      config.headers || {};

    config.headers.token =
      getToken();

    return config;
  }
);

/* =========================================
   ERROR NORMALIZADO
========================================= */

const normalizarError = (
  error,
  fallback
) => {
  return {
    ok: false,

    error:
      error?.response?.data
        ?.error ||
      error?.response?.data
        ?.message ||
      error?.message ||
      fallback ||
      "Error de comunicación",
  };
};

/* =========================================
   PREVIEW
   No guarda nada
========================================= */

export const previewPeajes =
  async ({
    proveedor,
    concesionario = "",
    nombreArchivo,
    filas,
  }) => {
    try {
      const { data } =
        await api.post(
          "/peajes/preview",
          {
            proveedor,
            concesionario,
            nombreArchivo,
            filas,
          }
        );

      return data;
    } catch (error) {
      return normalizarError(
        error,
        "No se pudo analizar el archivo."
      );
    }
  };

/* =========================================
   IMPORTAR
========================================= */

export const importarPeajes =
  async ({
    proveedor,
    concesionario = "",
    nombreArchivo,
    filas,
  }) => {
    try {
      const { data } =
        await api.post(
          "/peajes/importar",
          {
            proveedor,
            concesionario,
            nombreArchivo,
            filas,
          }
        );

      return data;
    } catch (error) {
      return normalizarError(
        error,
        "No se pudo importar el archivo."
      );
    }
  };

/* =========================================
   CONSUMOS
========================================= */

export const getConsumosPeajes =
  async (params = {}) => {
    try {
      const { data } =
        await api.get(
          "/peajes/consumos",
          {
            params,
          }
        );

      return data;
    } catch (error) {
      return normalizarError(
        error,
        "No se pudieron obtener los consumos."
      );
    }
  };

/* =========================================
   IMPORTACIONES
========================================= */

export const getImportacionesPeajes =
  async () => {
    try {
      const { data } =
        await api.get(
          "/peajes/importaciones"
        );

      return data;
    } catch (error) {
      return normalizarError(
        error,
        "No se pudo obtener el historial de importaciones."
      );
    }
  };

/* =========================================
   RESÚMENES / FACTURAS
========================================= */

/*
  Listado.

  Filtros opcionales:
  {
    proveedor,
    estado
  }
*/

export const getResumenesPeajes =
  async (params = {}) => {
    try {
      const { data } =
        await api.get(
          "/peajes/resumenes",
          {
            params,
          }
        );

      return data;
    } catch (error) {
      return normalizarError(
        error,
        "No se pudieron obtener los resúmenes."
      );
    }
  };

/* =========================================
   CREAR RESUMEN / FACTURA
========================================= */

export const crearResumenPeaje =
  async (payload) => {
    try {
      const { data } =
        await api.post(
          "/peajes/resumenes",
          payload
        );

      return data;
    } catch (error) {
      return normalizarError(
        error,
        "No se pudo crear el resumen de peaje."
      );
    }
  };

/* =========================================
   EDITAR RESUMEN / FACTURA
========================================= */

export const editarResumenPeaje =
  async (
    id,
    payload
  ) => {
    try {
      if (!id) {
        return {
          ok: false,
          error:
            "Falta el identificador del resumen.",
        };
      }

      const { data } =
        await api.put(
          `/peajes/resumenes/${id}`,
          payload
        );

      return data;
    } catch (error) {
      return normalizarError(
        error,
        "No se pudo modificar el resumen de peaje."
      );
    }
  };

/* =========================================
   CONSUMOS DISPONIBLES PARA RESUMEN
========================================= */

/*
  Ejemplo:

  getConsumosDisponiblesPeajes({
    resumenId,
    proveedor,
    desde,
    hasta,
    patente,
    concesionario
  })
*/

export const getConsumosDisponiblesPeajes =
  async (params = {}) => {
    try {
      const { data } =
        await api.get(
          "/peajes/consumos-disponibles",
          {
            params,
          }
        );

      return data;
    } catch (error) {
      return normalizarError(
        error,
        "No se pudieron obtener los consumos disponibles."
      );
    }
  };

/* =========================================
   CONSUMOS VINCULADOS A UN RESUMEN
========================================= */

export const getConsumosResumenPeaje =
  async (id) => {
    try {
      if (!id) {
        return {
          ok: false,
          error:
            "Falta el identificador del resumen.",
        };
      }

      const { data } =
        await api.get(
          `/peajes/resumenes/${id}/consumos`
        );

      return data;
    } catch (error) {
      return normalizarError(
        error,
        "No se pudieron obtener los consumos del resumen."
      );
    }
  };

/* =========================================
   VINCULAR CONSUMOS A RESUMEN
========================================= */

/*
  consumos debe ser un array de ids:

  [
    "ObjectId",
    "ObjectId",
    ...
  ]

  Esta llamada reemplaza la selección
  completa del resumen.
*/

export const vincularConsumosResumenPeaje =
  async (
    id,
    consumos = []
  ) => {
    try {
      if (!id) {
        return {
          ok: false,
          error:
            "Falta el identificador del resumen.",
        };
      }

      if (
        !Array.isArray(
          consumos
        )
      ) {
        return {
          ok: false,
          error:
            "La lista de consumos es inválida.",
        };
      }

      const { data } =
        await api.put(
          `/peajes/resumenes/${id}/consumos`,
          {
            consumos,
          }
        );

      return data;
    } catch (error) {
      return normalizarError(
        error,
        "No se pudieron vincular los consumos al resumen."
      );
    }
  };

/* =========================================
   PAGAR RESUMEN
========================================= */

/*
  Ejemplos de payload:

  CAJA
  {
    medioPago: "CAJA",
    caja: "...",
    fechaPago: "2026-09-17",
    importe: 1000
  }

  CUENTA BANCARIA
  {
    medioPago: "CUENTA_BANCARIA",
    cuentaBancaria: "...",
    fechaPago: "2026-09-17",
    importe: 1000
  }

  CHEQUE / ECHEQ existente
  {
    medioPago: "CHEQUE",
    carteraItem: "...",
    fechaPago: "2026-09-17",
    importe: 1000
  }

  CHEQUE nuevo
  {
    medioPago: "CHEQUE",
    nroCheque: "123456",
    banco: "...",
    fechaCheque: "...",
    fechaPago: "...",
    importe: 1000
  }

  ECHEQ nuevo
  {
    medioPago: "ECHEQ",
    idICheq: "...",
    banco: "...",
    fechaLiquidacion: "...",
    fechaPago: "...",
    importe: 1000
  }
*/

export const pagarResumenPeaje =
  async (
    id,
    payload
  ) => {
    try {
      if (!id) {
        return {
          ok: false,
          error:
            "Falta el identificador del resumen.",
        };
      }

      const { data } =
        await api.post(
          `/peajes/resumenes/${id}/pagar`,
          payload
        );

      return data;
    } catch (error) {
      return normalizarError(
        error,
        "No se pudo registrar el pago del resumen."
      );
    }
  };

/* =========================================
   ANULAR PAGO
========================================= */

export const anularPagoResumenPeaje =
  async (id) => {
    try {
      if (!id) {
        return {
          ok: false,
          error:
            "Falta el identificador del resumen.",
        };
      }

      const { data } =
        await api.post(
          `/peajes/resumenes/${id}/anular-pago`
        );

      return data;
    } catch (error) {
      return normalizarError(
        error,
        "No se pudo anular el pago del resumen."
      );
    }
  };